import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { isPhoneQuery, normalizePhone } from '../lib/format.js';
import { formatKontaktName, ROLLE_LABEL } from '../lib/kontakt.js';
import { fachgebietAnzeige } from '../lib/fachrichtung.js';
import { praxisAnzeige } from '../lib/praxis.js';

const TYP_LABEL = { praxis: 'Arztpraxis', klinik: 'Klinik' };

export default function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [praxen, setPraxen] = useState([]);
  const [kontakte, setKontakte] = useState([]);

  // Debounced PB-Suche.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setPraxen([]);
      setKontakte([]);
      setOpen(false);
      return undefined;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        // Telefon-Eingaben (keine Buchstaben, mind. zwei Ziffern) brauchen
        // einen ziffernreduzierten Vergleich — der PB-Operator '~' schaut
        // nur auf den Rohstring und findet "414" nicht in "040 – 41 46 0994",
        // weil Trenner dazwischenliegen. Deshalb laden wir bei Phone-Queries
        // alle Records mit nicht-leerem Telefonfeld und filtern clientseitig.
        if (isPhoneQuery(q)) {
          const qDigits = normalizePhone(q);
          const [praxenList, kontakteList] = await Promise.all([
            pb.collection('praxen').getFullList({
              filter: 'telefon != ""',
              sort: 'name',
              batch: 500,
            }),
            pb.collection('kontakte').getFullList({
              filter: 'telefon != "" || mobil != ""',
              sort: 'nachname',
              expand: 'praxis',
              batch: 500,
            }),
          ]);

          const praxenMatched = praxenList
            .filter((p) => normalizePhone(p.telefon).includes(qDigits))
            .slice(0, 6);
          const kontakteMatched = kontakteList
            .filter(
              (k) =>
                normalizePhone(k.telefon).includes(qDigits) ||
                normalizePhone(k.mobil).includes(qDigits)
            )
            .slice(0, 6);

          if (!cancelled) {
            setPraxen(praxenMatched);
            setKontakte(kontakteMatched);
            setOpen(true);
          }
          return;
        }

        // Reguläre Textsuche serverseitig.
        // pb.filter eskapiert die Eingabe → kein Filter-Injection-Risiko.
        const praxenFilter = pb.filter(
          'name ~ {:q} || name_zusatz ~ {:q} || ort ~ {:q} || fachgebiet ~ {:q} || ' +
            'fachgebiet_neu ~ {:q} || verbund ~ {:q} || verbund_sonstiges ~ {:q}',
          { q }
        );
        const kontakteFilter = pb.filter(
          'vorname ~ {:q} || nachname ~ {:q} || email ~ {:q}',
          { q }
        );

        const [praxenResp, kontakteResp] = await Promise.all([
          pb.collection('praxen').getList(1, 6, { filter: praxenFilter, sort: 'name' }),
          pb.collection('kontakte').getList(1, 6, {
            filter: kontakteFilter,
            sort: 'nachname',
            expand: 'praxis',
          }),
        ]);

        if (!cancelled) {
          setPraxen(praxenResp.items);
          setKontakte(kontakteResp.items);
          setOpen(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Globale Suche fehlgeschlagen', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  // Klick außerhalb schließt das Dropdown.
  useEffect(() => {
    function onClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      return () => document.removeEventListener('mousedown', onClick);
    }
    return undefined;
  }, [open]);

  function reset() {
    setQuery('');
    setOpen(false);
    setPraxen([]);
    setKontakte([]);
  }

  function gotoPraxis(praxisId) {
    navigate(`/praxen/${praxisId}`);
    reset();
    inputRef.current?.blur();
  }

  const totalResults = praxen.length + kontakte.length;
  const showDropdown = open && query.trim().length > 0;

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="search"
          placeholder="Praxen oder Kontakte suchen …"
          className="input pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (query.trim()) setOpen(true);
          }}
        />
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[480px] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {loading && (
            <div className="px-4 py-3 text-sm text-slate-500">Suche …</div>
          )}

          {!loading && totalResults === 0 && (
            <div className="px-4 py-6 text-center text-sm text-slate-500">
              Keine Treffer für &bdquo;{query}&ldquo;.
            </div>
          )}

          {praxen.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Praxen &amp; Kliniken
              </div>
              <ul className="divide-y divide-slate-100">
                {praxen.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => gotoPraxis(p.id)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-primary-50"
                    >
                      <div>
                        <div className="text-sm font-medium text-slate-900">{praxisAnzeige(p)}</div>
                        <div className="text-xs text-slate-500">
                          {[TYP_LABEL[p.typ] ?? p.typ, p.ort, fachgebietAnzeige(p)]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {kontakte.length > 0 && (
            <div>
              <div className="border-t border-slate-100 px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Kontakte
              </div>
              <ul className="divide-y divide-slate-100">
                {kontakte.map((k) => {
                  const praxis = k.expand?.praxis;
                  const fullName = formatKontaktName(k);
                  return (
                    <li key={k.id}>
                      <button
                        type="button"
                        onClick={() => praxis && gotoPraxis(praxis.id)}
                        disabled={!praxis}
                        className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-primary-50 disabled:opacity-60"
                      >
                        <div>
                          <div className="text-sm font-medium text-slate-900">{fullName}</div>
                          <div className="text-xs text-slate-500">
                            {[ROLLE_LABEL[k.rolle] ?? k.rolle, k.email, praxis?.name]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
